<template>
  <div :class="newDesign && 'new-design'">
    <div
      v-if="showFilters"
      class="row form-input q-mb-sm items-end justify-between"
    >
      <div
        class="label"
        :class="{ 'field-heading': newDesign }"
      >
        <span>{{ label }}</span>
        <span
          v-if="showCount"
          class="soft"
        >
          [{{ recipients.length }}]
        </span>
      </div>

      <InputText
        v-if="filterEnabled"
        v-model="filter"
        clearable
        :new-design="newDesign"
        :placeholder="$t('email_field.filter_recipients')"
      >
        <template #prepend>
          <QIcon
            color="grey"
            name="search"
            size="16px"
          />
        </template>
      </InputText>

      <BaseButton
        v-if="canClearRecipients && !disabled"
        dense
        flat
        icon="cancel"
        :new-design="newDesign"
        size="sm"
        @click="clearRecipients"
      >
        <StreemTooltip>{{
          $t("email_field.clear_all_recipients")
        }}</StreemTooltip>
      </BaseButton>
    </div>

    <VueTagsInput
      ref="tagsInput"
      v-model="email"
      :add-on-key="[13, 188, 9, 32]"
      autocomplete="super-off"
      :autocomplete-items="filteredEmails"
      class="streem-input"
      :class="newDesign && 'input-wrapper new-design'"
      :disabled="disabled"
      :is-duplicate="isDuplicateTag"
      :placeholder="$t('email_field.add_email')"
      :separators="[',', ';', ' ']"
      :tags="tags"
      :validation="validation"
      @before-adding-tag="beforeAddTag"
      @tags-changed="tagChange"
    >
      <template #autocomplete-item="props">
        <div class="row items-center">
          <span
            class="flex-1"
            @click="props.performAdd(props.item)"
          >
            {{ props.item.text }}
          </span>
          <QBtn
            flat
            icon="ion-close-circle-outline"
            size="sm"
            @click="removeRecipient(props.item.text)"
          />
        </div>
      </template>

      <template #tag-actions="props">
        <QIcon
          name="ion-md-close-circle"
          style="margin-left: 4px"
          @click="props.performDelete(props.index)"
        />
      </template>
    </VueTagsInput>

    <div class="q-gutter-x-sm flex justify-end">
      <div
        v-if="copyRecipients"
        class="q-mt-sm"
      >
        <BaseButton
          :color="buttonColor"
          dense
          icon="file_copy"
          :label="copyButtonLabel"
          :new-design="newDesign"
          size="sm"
          @click="copyRecipientsToClipboard(tags)"
        >
          <StreemTooltip v-if="!buttonLabels">
            {{ $t("email_field.copy_recipients_to_clipboard") }}
          </StreemTooltip>
        </BaseButton>
      </div>

      <div
        v-if="csvExportRecipients"
        class="q-mt-sm"
      >
        <BaseButton
          :color="buttonColor"
          dense
          icon="download"
          :label="exportButtonLabel"
          :new-design="newDesign"
          size="sm"
          @click="exportRecipients(tags)"
        >
          <StreemTooltip v-if="!buttonLabels">
            {{ $t("email_field.export_recipients_to_csv") }}
          </StreemTooltip>
        </BaseButton>
        <VueJsonCsv
          v-show="false"
          ref="csv"
          class="col"
          :data="formattedCSVData"
          @export-finished="exportFinished"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { createTag, VueTagsInput } from "@sipec/vue3-tags-input";
import VueJsonCsv from "vue-json-csv";

import { BaseButton, InputText } from "shared/components/base";
import useClipboard from "shared/composables/useClipboard";
import transformKeys from "shared/helpers/transformKeys";

export default {
  name: "EmailField",
  components: {
    VueTagsInput,
    VueJsonCsv,
    InputText,
    BaseButton,
  },
  props: {
    recipients: {
      type: Array,
      required: true,
    },
    label: {
      type: String,
      default: "",
    },
    useLastEmailRecipients: {
      type: Boolean,
    },
    filterEnabled: {
      type: Boolean,
    },
    csvExportRecipients: {
      type: Boolean,
    },
    copyRecipients: {
      type: Boolean,
    },
    showCount: {
      type: Boolean,
    },
    canClearRecipients: {
      type: Boolean,
    },
    userId: {
      type: [Number, String],
      default: "",
    },
    distributionLists: {
      type: Array,
      required: false,
      default: () => [],
    },
    newDesign: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
    buttonColor: {
      type: String,
      default: "primary",
    },
    buttonLabels: {
      type: Boolean,
    },
  },
  emits: ["update:recipients"],
  setup() {
    const { copyToClipboard } = useClipboard();

    return { copyToClipboard };
  },
  data() {
    return {
      email: "",
      emails: [],
      filter: "",
      dataReady: false,
      validation: [
        {
          classes: "email",
          rule: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
          disableAdd: true,
          type: "email",
        },
      ],
    };
  },
  computed: {
    formattedCSVData() {
      if (this.dataReady) this.$refs.csv.generate();

      return this.tags.map((tag) => ({ recipient: tag.recipients }));
    },
    filteredEmails() {
      if (!this.email || this.hasMultipleEmails(this.email)) {
        return [];
      }

      try {
        const escapedEmail = this.email.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(escapedEmail, "i");

        return this.emails
          .filter((email) => regex.test(email))
          .map((text) => ({ text }));
      } catch {
        return [];
      }
    },
    tags() {
      const recipientTags = this.recipients.map((tag) => ({
        text: tag,
        recipients: tag,
      }));

      this.localDistributionLists.forEach((distributionList) => {
        const tagIndex = recipientTags.findIndex(
          (tag) => tag.text === distributionList.referenceEmail
        );

        recipientTags[tagIndex] = {
          text: this.$t("email_field.recipient_tag_text", {
            name: distributionList.name,
            recipientsCount: distributionList.recipients.length,
          }),
          recipients: [...distributionList.recipients],
          reference: distributionList.referenceEmail,
          linked: true,
        };
      });

      return recipientTags.map((tag) => ({
        text: tag.text,
        classes: this.getTagClass(tag),
        recipients: tag.recipients,
        reference: tag.reference || tag.text,
      }));
    },
    localDistributionLists() {
      return this.distributionLists.map((list) =>
        transformKeys(list, "camelCase")
      );
    },
    showFilters() {
      return (
        this.label ||
        this.filterEnabled ||
        this.filter ||
        (this.canClearRecipients && !this.disabled)
      );
    },
    copyButtonLabel() {
      if (this.buttonLabels) {
        return this.$t("email_field.copy_recipients_to_clipboard");
      }

      return "";
    },
    exportButtonLabel() {
      if (this.buttonLabels) {
        return this.$t("email_field.export_recipients_to_csv");
      }

      return "";
    },
  },
  watch: {
    filter() {
      // Handling the filter directly in the DOM for better
      // performance when dealing with large recipient lists
      const tags = [];

      document.querySelectorAll(".ti-tag").forEach((tag) =>
        tags.push({
          classList: tag.classList,
          text: tag.innerText.toLowerCase(),
        })
      );

      tags.forEach((tag) => {
        if (!tag.text.includes(this.filter.toLowerCase())) {
          tag.classList.add("hidden");
        } else {
          tag.classList.remove("hidden");
        }
      });
    },
  },
  async mounted() {
    if (this.userId) {
      const response = await this.$streemApiV1.get(
        `users/${this.userId}/email_recipient_lists`
      );

      this.emails = [];

      response.data.forEach((recipientList) => {
        recipientList.recipient_emails.forEach((email) => {
          if (!this.emails.includes(email)) {
            this.emails.push(email);
          }
        });
      });

      if (this.useLastEmailRecipients && response.data.length) {
        response.data
          .slice(-1)
          .pop()
          .recipient_emails.forEach((email) => {
            if (!this.recipients.includes(email)) {
              this.recipients.push(email);
            }
          });
      }
    }
  },
  methods: {
    async removeRecipient(recipient) {
      try {
        await this.$streemApiV1.put(`users/${this.userId}/remove_recipient`, {
          params: { recipient },
        });

        this.emails = this.emails.filter((email) => email !== recipient);
      } catch {
        this.$q.notify(this.$t("email_field.remove_recipient_error"));
      }
    },
    clearRecipients() {
      this.recipients = [];
    },
    exportFinished() {
      this.dataReady = false;
    },
    tagChange(event) {
      const recipients = [].concat(
        ...event.map((tag) => tag.reference || tag.text)
      );

      this.$emit("update:recipients", recipients);
    },
    getTagClass(item) {
      if (item.linked) return "tag-linked-list";

      return "tag-recipient";
    },
    /** @public */
    forceUpdate() {
      this.validateInput();
    },
    /** @public */
    focus() {
      this.$refs.tagsInput.$el.querySelector("input").focus();
    },
    validateInput() {
      if (this.email.length > 0) {
        const validatedTag = createTag(
          this.email,
          this.recipients,
          this.validation
        );

        this.beforeAddTag(validatedTag);
      }
    },
    beforeAddTag({ tag }) {
      if (!tag) return;

      if (
        this.recipients.includes(tag.text) ||
        !["valid", "ti-valid"].includes(tag.tiClasses[0])
      ) {
        return;
      }

      this.email = "";
      this.recipients.push(tag.text);
      this.$emit("update:recipients", [...new Set(this.recipients)]);
    },
    isDuplicateTag(_, tag) {
      return (
        this.recipients.filter((recipient) => tag.text === recipient).length > 1
      );
    },
    copyRecipientsToClipboard(recipients) {
      this.copyToClipboard(
        recipients.map((recipient) => recipient.text).toString()
      );
    },
    exportRecipients() {
      this.dataReady = true;
    },
    hasMultipleEmails(text) {
      if (!text || text.trim().length === 0) return false;
      const separators = [",", ";", "\n", "\r", "\t"];

      const separatorCount = separators.reduce(
        (count, separator) => count + (text.split(separator).length - 1),
        0
      );

      if (separatorCount > 0) {
        const parts = text
          .split(/[,;\n\r\t]+/)
          .map((part) => part.trim())
          .filter((part) => part.length > 0);

        return parts.length > 1;
      }

      const emailPattern =
        /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;

      const matches = text.match(emailPattern);

      if (!matches) return false;

      return matches.length > 1;
    },
  },
};
</script>

<style lang="scss" scoped>
.clearfix {
  clear: both;
}

.label {
  margin-right: auto;
}

.streem-input.vue-tags-input {
  &:not(.new-design) {
    max-width: none;
    background-color: $silver-white;

    :deep(.ti-new-tag-input) {
      background-color: #eee;
      font-size: 18px;
      color: #111;
      font-weight: normal;
    }

    :deep(.ti-input) {
      font-weight: normal;
      border: none;
    }

    :deep(.ti-tag) {
      background-color: var(--s-color-primary);

      &.tag-distributed-list {
        background-color: #6a6a6a;
      }

      &.tag-linked-list {
        background-color: #316899;
      }

      &.tag-recipient {
        background-color: var(--s-color-primary);
      }

      &.ti-invalid {
        background-color: var(--s-color-danger);
      }
    }

    :deep(.ti-deletion-mark) {
      background-color: $tv;
    }

    :deep(.ti-autocomplete) {
      font-weight: normal;
    }

    :deep(.ti-item.ti-selected-item) {
      background-color: var(--s-color-primary);
      color: white;
    }

    :deep(.ti-tags) {
      max-height: 400px;
      overflow-y: scroll;
    }
  }
}

.dropdown-list {
  padding: 0.285em 0;

  .item {
    flex-direction: column;
    align-items: start;

    small {
      margin-top: 2px;
    }
  }
}

.filter-recipients {
  background: #eee;
  font-weight: normal;
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }

  input {
    background: #eee;
    padding-left: 25px;
  }

  i {
    left: 15px;
    bottom: 2px;
    opacity: 0.6;
  }
}

.new-design {
  .streem-input.vue-tags-input {
    max-width: none;
    overflow: inherit;

    &.ti-focus {
      &:has(.ti-autocomplete) {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }

    :deep(.ti-tag) {
      &.tag-distributed-list {
        background-color: #6a6a6a;
      }

      &.tag-linked-list {
        background-color: #316899;
      }

      &.tag-recipient {
        background-color: var(--s-color-primary);
      }
    }

    :deep(.ti-item.ti-selected-item) {
      background-color: var(--s-color-primary);
    }

    :deep(.ti-tags) {
      max-height: 400px;
      overflow-y: auto;
    }

    :deep(.ti-input) {
      border: none;
      flex: 1;

      input {
        min-height: inherit;
      }
    }

    :deep(.ti-autocomplete) {
      border: 1px solid var(--s-input-focus-border);
      border-bottom-left-radius: var(--s-border-radius-md);
      border-bottom-right-radius: var(--s-border-radius-md);
      left: -1px;
      top: 100%;
      width: calc(100% + 2px);
    }
  }
}
</style>
